import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { api } from '@/shared/api/client';

// ONE rows-per-page preference for the whole app: pick 25 on any list and
// every other list (server-paginated, client-paginated, or a DataTable's
// inline fallback pager) shows 25 too. Server is the source of truth
// (user-preferences/pagination) so the choice follows the user across
// machines; localStorage is just a paint-order cache, same pattern as
// navPrefsStore.
const STORAGE_KEY = 'mrpcomp:page-size:v1';
const NAMESPACE = 'pagination';
const SAVE_DEBOUNCE_MS = 500;

export const PAGE_SIZES = [20, 25, 30, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

function normalize(v: unknown): number {
  const n = Number(v);
  return PAGE_SIZES.includes(n) ? n : DEFAULT_PAGE_SIZE;
}

function loadCache(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw != null) return normalize(JSON.parse(raw));
  } catch { /* ignore */ }
  return DEFAULT_PAGE_SIZE;
}

function saveCache(size: number) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(size)); }
  catch { /* best effort */ }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleServerSave(size: number) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    saveTimer = null;
    if (!useAuthStore.getState().user) return;
    try {
      await api.put(`/user-preferences/${NAMESPACE}`, { page_size: size });
    } catch { /* localStorage already has the truth — retry on next change */ }
  }, SAVE_DEBOUNCE_MS);
}

type PageSizeState = {
  pageSize: number;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  setPageSize: (n: number) => void;
};

type ServerRow = { value: { page_size?: number }; updated_at: string } | null;

export const usePageSizeStore = create<PageSizeState>((set, get) => ({
  pageSize: loadCache(),
  hydrated: false,
  hydrate: async () => {
    try {
      const row = await api.get<ServerRow>(`/user-preferences/${NAMESPACE}`);
      if (row && row.value) {
        const next = normalize(row.value.page_size);
        set({ pageSize: next, hydrated: true });
        saveCache(next);
        return;
      }
    } catch { /* fall through to cached/default */ }
    set({ hydrated: true });
  },
  setPageSize: (n) => {
    const next = normalize(n);
    if (next === get().pageSize) return;
    set({ pageSize: next });
    saveCache(next);
    scheduleServerSave(next);
  },
}));
